import Money from 'common/Money';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {AccountInlineDisplay} from 'src/AccountInlineDisplay';
import {AmountDisplay} from 'src/AmountDisplay';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import {totalsByCurrency} from 'src/lib/currencyTotals';
import {formatListDate} from 'src/lib/displayDate';
import {cn} from 'src/lib/utils';
import type {
  BasicRawTransactionFragment,
  DetailTransactionFragment,
} from '@transactions/graphql';

// rawTransaction may be absent when Apollo cache was overwritten by a mutation
// returning UnifiedTransaction (which omits consumptions.rawTransaction)
type Consumption = Omit<
  DetailTransactionFragment['consumptions'][number],
  'rawTransaction'
> & {
  rawTransaction?: BasicRawTransactionFragment;
};

type Props = {
  consumptions: Consumption[];
  currency: string;
  /** The Net Amount footer; off where the amount is already on screen. */
  showTotal?: boolean;
  /** A line to pick out. */
  highlightId?: string | null;
};

const getSource = (
  rt: BasicRawTransactionFragment,
): 'Plaid' | 'Venmo' | 'Manual' => {
  if (rt.plaidId || rt.plaidInvestmentTransactionId) return 'Plaid';
  if (rt.venmoId) return 'Venmo';
  return 'Manual';
};

const ConsumptionsList = ({
  consumptions,
  currency,
  showTotal = true,
  highlightId,
}: Props) => {
  const formatMoney = useMoneyFormatter();
  if (consumptions.length === 0) {
    return (
      <div className="px-4 py-3 text-muted-foreground">No lines</div>
    );
  }

  // A conversion consumes raws in two currencies; totals must stay
  // per-currency (a single cross-currency sum would be meaningless).
  const netAmounts = totalsByCurrency(
    consumptions.map((c) => ({
      amount: c.amount,
      currency: c.rawTransaction?.currency ?? currency,
    })),
  );

  return (
    <div className="flex flex-col">
      {consumptions.map((consumption, index) => {
        const rt = consumption.rawTransaction;
        // Each consumed slice is denominated in its raw's own currency; the
        // parent transaction's currency is only a fallback when the raw is
        // absent from the cache.
        const consumptionAmount = Money.fromInt(
          consumption.amount,
          rt?.currency ?? currency,
        );

        if (!rt) {
          return (
            <Link
              key={`${consumption.rawTransactionId}-${index}`}
              to={`/raw-transactions/${consumption.rawTransactionId}`}
              className="flex cursor-pointer items-center justify-between gap-4 px-4 py-3 transition-colors not-last:border-b not-last:border-border hover:bg-muted/50"
            >
              <div className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                {consumption.rawTransactionId}
              </div>
              <AmountDisplay amount={consumptionAmount} />
            </Link>
          );
        }

        const fullAmount = Money.fromInt(rt.amount, rt.currency);
        const isPartial = consumption.amount !== rt.amount;
        const source = getSource(rt);
        const hasRemaining =
          rt.remainingAmount != null && rt.remainingAmount !== 0;

        return (
          <Link
            key={`${consumption.rawTransactionId}-${index}`}
            to={`/raw-transactions/${rt._id}`}
            aria-current={rt._id === highlightId ? 'true' : undefined}
            className={cn(
              'flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-3 transition-colors not-last:border-b not-last:border-border hover:bg-muted/50',
              // Blue, as a selected activity row is: the line you came from.
              rt._id === highlightId && 'bg-blue-600/10 dark:bg-blue-400/15',
            )}
          >
            <div className="flex min-w-0 flex-col gap-1.5">
              <div className="truncate text-foreground" title={rt.description}>
                {rt.description}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                <AccountInlineDisplay account={rt.account} />
                <span className="text-xs">{formatListDate(rt.date)}</span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {source}
                </span>
                {hasRemaining && (
                  <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-xs text-amber-600 dark:text-amber-400">
                    {formatMoney(
                      Money.fromInt(rt.remainingAmount!, rt.currency),
                    )}{' '}
                    remaining
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end justify-center gap-1 text-right">
              <AmountDisplay amount={consumptionAmount} />
              {isPartial && (
                <div className="text-xs text-muted-foreground">
                  of <AmountDisplay amount={fullAmount} variant="plain" inline />
                </div>
              )}
            </div>
          </Link>
        );
      })}
      {showTotal && (
        <div className="flex items-center justify-between gap-4 border-t border-border bg-muted/50 px-4 py-2">
          <div className="font-medium">Net Amount</div>
          <div className="flex items-center gap-2">
            {netAmounts.map((amount) => (
              <AmountDisplay key={amount.currency} amount={amount} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export {ConsumptionsList};
